import { type JSX } from 'react';
import { DocumentNode } from 'graphql';
import {
  ApolloError,
  OperationVariables,
  useQuery,
  QueryHookOptions,
} from '@apollo/client';
import ServerError from '../../ServerError/ServerError';
import EDAvenyProgress from '../../../BaseComponents/ProgressIndicators/AvenyProgress/EDAvenyProgress';

interface IComponentOptions {
  loadingComponent?: JSX.Element | null | undefined;
  noDataComponent?: JSX.Element | null | undefined;
  errorComponent?:
    | null
    | ((error: ApolloError) => JSX.Element | null | undefined);
}

interface IResult<DataType> {
  data: DataType | null;
  renderComponent: JSX.Element | null;
}

interface ICustomQueryOptions {
  setRefetch?: (refetchAnalys: () => void) => void;
}

const useHandledQuery = function <
  DataType = Record<string, unknown>,
  VariableType extends OperationVariables = Record<string, unknown>
>(
  query: DocumentNode,
  options: QueryHookOptions<DataType, VariableType>,
  components?: IComponentOptions,
  customOptions?: ICustomQueryOptions
): IResult<DataType> {
  const { loading, error, data, refetch } = useQuery<DataType, VariableType>(
    query,
    options
  );

  // Defaults for when components arent supplied (null is a valid value, and can be used to show nothing during loading etc)
  if (!components) {
    components = {};
  }
  if (components.loadingComponent === undefined) {
    components.loadingComponent = <EDAvenyProgress />;
  }
  if (components.errorComponent === undefined) {
    components.errorComponent = (error: ApolloError) => (
      <ServerError error={error} />
    );
  }
  if (components.noDataComponent === undefined) {
    components.noDataComponent = <span>No data received</span>;
  }

  if (!customOptions) {
    customOptions = {};
  }

  const {
    loadingComponent = null,
    errorComponent = null,
    noDataComponent = null,
  } = components;
  const { setRefetch = null } = customOptions;

  if (setRefetch) {
    setRefetch(refetch);
  }

  if (loading) {
    return {
      data: null,
      renderComponent: loadingComponent || null,
    };
  }

  if (error) {
    if (!errorComponent) {
      return {
        data: null,
        renderComponent: null,
      };
    } else {
      return {
        data: null,
        renderComponent: errorComponent(error) || null,
      };
    }
  }

  if (data) {
    return {
      data: data,
      renderComponent: null,
    };
  } else {
    return {
      data: null,
      renderComponent: noDataComponent || null,
    };
  }
};

export default useHandledQuery;
